import {
  LandingAuthorFragment,
  Maybe,
  LandingMetadataFragment,
} from "types/api";
import styles from "./LandingMetadata.module.scss";
import gql from "graphql-tag";
import { formatArticleDate } from "utils/date";

/**
 * Refers to the different parts of the page that can have click tracking
 * applied to them.
 */
type LandingPageAnalyticsSection = "featured" | "project" | "river";

function* createAuthors(
  authors: Array<Maybe<LandingAuthorFragment>>,
  analyticsSection: LandingPageAnalyticsSection,
  riverIndex?: number
) {
  let i = 0;

  while (i < authors.length) {
    const author = authors[i];

    if (authors.length > 1 && i === authors.length - 1) {
      yield " and ";
    }

    if (author) {
      yield (
        <Author
          {...author}
          key={author.slug}
          analyticsSection={analyticsSection}
          riverIndex={riverIndex}
        />
      );
    }

    if (authors.length > 2 && i !== authors.length - 1) {
      yield ", ";
    }

    i += 1;
  }
}

const Author = ({
  slug,
  displayName,
  analyticsSection,
  riverIndex,
}: LandingAuthorFragment & {
  analyticsSection: LandingPageAnalyticsSection;
  riverIndex?: number;
}) => {
  return (
    <a
      href={`https://www.theatlantic.com/author/${slug}`}
      className={styles.author}
      data-action={`click link - ${analyticsSection} - author${
        riverIndex ? ` ${riverIndex}` : ""
      }`}
    >
      {displayName}
    </a>
  );
};

const LandingMetadata = ({
  authors,
  datePublished,
  analyticsSection,
  riverIndex,
  ...props
}: LandingMetadataFragment & {
  analyticsSection: LandingPageAnalyticsSection;
  riverIndex?: number;
}) => {
  return (
    <p className={styles.root}>
      {authors.length > 0 && (
        <span className={styles.byline}>
          {Array.from(createAuthors(authors, analyticsSection, riverIndex))}
        </span>
      )}
      {props.__typename === "MagazineArticle" ? (
        <a href={props.issue.url} className={styles.issue}>
          {props.issue.issueName}
        </a>
      ) : (
        datePublished && (
          <time className={styles.datePublished} dateTime={datePublished}>
            {formatArticleDate(datePublished)}
          </time>
        )
      )}
    </p>
  );
};

LandingMetadata.fragment = gql`
  fragment landingIssue on MagazineArticle {
    issue {
      issueName
      url
    }
  }

  fragment landingAuthor on Author {
    id
    slug
    displayName
  }

  fragment landingMetadata on PromoItem {
    datePublished
    authors {
      ...landingAuthor
    }
    ...landingIssue
  }
`;

export default LandingMetadata;
